import type { DegradationPreference, IceServerConfig, VideoCodecPreference } from '../core/model';
import type { Logger } from '../core/services';
import type { EncodingOptions } from './api';

const SCOPE = 'net.media';

/** Merges ICE server lists (project settings, signalling server, WHIP Link headers) without duplicates. */
export function mergeIceServers(...lists: Array<readonly IceServerConfig[] | undefined>): RTCIceServer[] {
  const seen = new Set<string>();
  const out: RTCIceServer[] = [];
  for (const list of lists) {
    for (const server of list ?? []) {
      const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
      if (urls.length === 0) continue;
      const key = JSON.stringify([urls, server.username ?? '', server.credential ?? '']);
      if (seen.has(key)) continue;
      seen.add(key);
      const entry: RTCIceServer = { urls };
      if (server.username !== undefined) entry.username = server.username;
      if (server.credential !== undefined) entry.credential = server.credential;
      out.push(entry);
    }
  }
  return out;
}

const CODEC_MIME: Record<Exclude<VideoCodecPreference, 'auto'>, string> = {
  vp8: 'video/vp8',
  vp9: 'video/vp9',
  h264: 'video/h264',
  av1: 'video/av1',
};

/**
 * Moves the preferred codec to the front and keeps everything else (including
 * RTX/FEC) in the browser's order, so negotiation can still fall back. Returns
 * null when there is nothing to change: 'auto', or a codec the browser lacks.
 */
export function orderCodecs<T extends { mimeType: string; sdpFmtpLine?: string }>(
  codecs: readonly T[],
  preference: VideoCodecPreference,
  /** Formats (see codec-choice `formatKey`) of the preferred codec that go before its others. */
  first: ReadonlySet<string> = NO_FORMATS,
): T[] | null {
  if (preference === 'auto') return null;
  const mime = CODEC_MIME[preference];
  const preferred = codecs.filter((c) => c.mimeType.toLowerCase() === mime);
  if (preferred.length === 0) return null;
  const isFirst = (c: T): boolean => first.has(`${c.mimeType.toLowerCase()};${c.sdpFmtpLine ?? ''}`);
  return [
    ...preferred.filter(isFirst),
    ...preferred.filter((c) => !isFirst(c)),
    ...codecs.filter((c) => c.mimeType.toLowerCase() !== mime),
  ];
}

const NO_FORMATS: ReadonlySet<string> = new Set();

/**
 * The program is composed output: camera, screen and text mixed. The hint
 * follows the operator's degradation choice so encoder heuristics agree with it.
 */
export function contentHintFor(degradation: DegradationPreference): 'motion' | 'detail' {
  return degradation === 'maintain-resolution' ? 'detail' : 'motion';
}

/**
 * Asks the remote sender for stereo Opus by declaring `stereo=1` on our Opus
 * receive format. Without it browsers downmix a stereo program to mono.
 */
export function withStereoOpus(sdp: string): string {
  const payloadTypes = new Set<string>();
  for (const match of sdp.matchAll(/^a=rtpmap:(\d+) opus\/48000\/2\r?$/gim)) payloadTypes.add(match[1]!);
  if (payloadTypes.size === 0) return sdp;

  const withFmtp = new Set<string>();
  let out = sdp.replace(/^a=fmtp:(\d+) ([^\r\n]*)/gm, (line, pt: string, params: string) => {
    if (!payloadTypes.has(pt)) return line;
    withFmtp.add(pt);
    return /(^|;)\s*stereo=/.test(params) ? line : `a=fmtp:${pt} ${params};stereo=1`;
  });
  for (const pt of payloadTypes) {
    if (withFmtp.has(pt)) continue;
    out = out.replace(new RegExp(`^(a=rtpmap:${pt} opus/48000/2)(\\r?)$`, 'im'), `$1$2\na=fmtp:${pt} stereo=1$2`);
  }
  return out;
}

/** Transceivers that have had a non-default preference applied and may need it cleared again. */
const preferred = new WeakSet<RTCRtpTransceiver>();

export interface ProgramSenders {
  video: RTCRtpTransceiver;
  audio: RTCRtpTransceiver;
}

/** Tracks whose contentHint this module set, so it only ever overrides its own choice. */
const hinted = new WeakSet<MediaStreamTrack>();

function hint(track: MediaStreamTrack | null, encoding: EncodingOptions): void {
  if (!track || track.kind !== 'video') return;
  if (track.contentHint !== '' && !hinted.has(track)) return;
  track.contentHint = contentHintFor(encoding.degradation);
  hinted.add(track);
}

/**
 * Capability lists do not change during a page's life, and asking for them is a synchronous
 * call into the browser's codec factories. Asked once, not once per joining peer.
 */
let videoCapabilities: Array<RTCRtpCapabilities | null | undefined> | null = null;

export function applyCodecPreference(
  transceiver: RTCRtpTransceiver,
  preference: VideoCodecPreference,
  logger: Logger,
  firstFormats: ReadonlySet<string> = NO_FORMATS,
): void {
  if (typeof transceiver.setCodecPreferences !== 'function') return;
  // The spec validates preferences against receive capabilities; older engines used the send
  // list. Try both rather than sniffing versions.
  // 'auto' on a fresh transceiver is already the browser default; there is nothing to set.
  if (preference === 'auto' && !preferred.has(transceiver)) return;
  preferred.add(transceiver);
  let sources = videoCapabilities;
  if (!sources) {
    sources = [RTCRtpReceiver.getCapabilities?.('video'), RTCRtpSender.getCapabilities?.('video')];
    // Only a real answer is worth remembering.
    if (sources.some((c) => c)) videoCapabilities = sources;
  }
  let failure: unknown = null;
  for (const capabilities of sources) {
    if (!capabilities) continue;
    try {
      // An empty list restores the browser default, which is what 'auto' or a missing codec means.
      transceiver.setCodecPreferences(orderCodecs(capabilities.codecs, preference, firstFormats) ?? []);
      return;
    } catch (err) {
      failure = err;
    }
  }
  if (failure) logger.log('warn', SCOPE, `could not apply codec preference "${preference}"`, failure);
}

/**
 * Adds one video and one audio transceiver for the program. They are created
 * even when the stream currently lacks a track, so a later replaceTrack needs no
 * renegotiation.
 */
export function addProgramTransceivers(
  pc: RTCPeerConnection,
  stream: MediaStream,
  direction: 'sendonly' | 'sendrecv',
  encoding: EncodingOptions,
  logger: Logger,
): ProgramSenders {
  // `encoding.codec` may still be 'auto' here; callers that resolve 'auto' to a concrete codec
  // apply it with applyCodecPreference before their first offer.
  const videoTrack = stream.getVideoTracks()[0] ?? null;
  const audioTrack = stream.getAudioTracks()[0] ?? null;
  hint(videoTrack, encoding);
  const video = pc.addTransceiver(videoTrack ?? 'video', {
    direction,
    streams: [stream],
    sendEncodings: encoding.videoKbps > 0 ? [{ maxBitrate: encoding.videoKbps * 1000 }] : [{}],
  });
  const audio = pc.addTransceiver(audioTrack ?? 'audio', {
    direction,
    streams: [stream],
    sendEncodings: encoding.audioKbps > 0 ? [{ maxBitrate: encoding.audioKbps * 1000 }] : [{}],
  });
  applyCodecPreference(video, encoding.codec, logger);
  return { video, audio };
}

async function setSenderLimits(
  sender: RTCRtpSender,
  kbps: number,
  degradation: DegradationPreference | null,
): Promise<void> {
  const params = sender.getParameters();
  if (!params.encodings || params.encodings.length === 0) params.encodings = [{}];
  for (const encoding of params.encodings) {
    if (kbps > 0) encoding.maxBitrate = kbps * 1000;
    else delete encoding.maxBitrate;
  }
  if (degradation) params.degradationPreference = degradation;
  await sender.setParameters(params);
}

/** Applies bitrate caps and degradation preference to live senders; safe to call repeatedly. */
export async function applyEncoding(
  senders: ProgramSenders,
  encoding: EncodingOptions,
  logger: Logger,
): Promise<void> {
  hint(senders.video.sender.track, encoding);
  const results = await Promise.allSettled([
    setSenderLimits(senders.video.sender, encoding.videoKbps, encoding.degradation),
    setSenderLimits(senders.audio.sender, encoding.audioKbps, null),
  ]);
  for (const result of results) {
    // setParameters rejects on a closed connection; that is expected during teardown.
    if (result.status === 'rejected' && senders.video.sender.transport?.state !== 'closed') {
      logger.log('warn', SCOPE, 'setParameters failed', result.reason);
    }
  }
}

export async function replaceProgramTracks(
  senders: ProgramSenders,
  stream: MediaStream,
  encoding: EncodingOptions,
): Promise<void> {
  const videoTrack = stream.getVideoTracks()[0] ?? null;
  hint(videoTrack, encoding);
  await Promise.all([
    senders.video.sender.replaceTrack(videoTrack),
    senders.audio.sender.replaceTrack(stream.getAudioTracks()[0] ?? null),
  ]);
}

export function closePeerConnection(pc: RTCPeerConnection): void {
  pc.onicecandidate = null;
  pc.onconnectionstatechange = null;
  pc.oniceconnectionstatechange = null;
  pc.onicegatheringstatechange = null;
  pc.ontrack = null;
  pc.close();
}

/** RTCIceCandidate reduced to the fields the signalling protocol carries. */
export function toWireCandidate(candidate: RTCIceCandidate | null): {
  candidate: string;
  sdpMid: string | null;
  sdpMLineIndex: number | null;
  usernameFragment: string | null;
} | null {
  if (!candidate) return null;
  return {
    candidate: candidate.candidate,
    sdpMid: candidate.sdpMid,
    sdpMLineIndex: candidate.sdpMLineIndex,
    usernameFragment: candidate.usernameFragment,
  };
}
